import type { CellContext, Row } from '@tanstack/react-table';
import * as React from 'react';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import { compose } from 'redux';

import type { W_WorkOrderReport_UsersForTimeCardViewTable_VM, W_WorkOrderReport_UsersForTimeCardViewTable_VM_Row } from 'ab-api/web/workOrderReport/timeCardViewTable/viewModel';

import { bemElement } from 'af-utils/bem.util';

import TableNameEnum from 'ab-enums/tableName.enum';
import { TimeEntriesFilterType } from 'ab-enums/timeEntries.enum';

import type { TableQuery } from 'ab-common/dataStructures/tableQuery';

import { Button } from '@acceligentllc/storybook';

import Dropdown from 'af-components/Controls/Dropdown';
import RectangleButton from 'af-components/MultipleOptionsButton/RectangleButton';
import TableNew from 'af-components/Table';
import type { TableRef } from 'af-components/Table';
import TextCell from 'af-components/Table/Cells/TextCell';
import type { TableProps } from 'af-components/Table/types';
import Tooltip from 'af-components/Tooltip';

import * as FieldReportActions from 'af-actions/fieldReport';
import * as TimeCardActions from 'af-actions/timeCard';

import TimeCardBodyWrapper from './SidePanel/TimeCardBodyWrapper';
import UnassignedEntriesWrapper from './SidePanel/UnassignedEntriesWrapper';
import styles from './styles.module.scss';

type EntriesFilterOptions = { option: TimeEntriesFilterType; label: string; };
interface EntryIdWorkOrderIdCombination {
	[entryId: number]: number;
}

const connector = connect(null, mapDispatchToProps());

type Props = ConnectedProps<typeof connector>;

const ENTRIES_OPTIONS: EntriesFilterOptions[] = [
	{
		option: TimeEntriesFilterType.ALL, label: 'All',
	},
	{
		option: TimeEntriesFilterType.UNASSIGNED, label: 'Unassigned',
	},
];

const TimeCardViewTable = (props: Props) => {
	const {
		fetchListOfUsersWithEntries,
		fetchAccountTimeCard,
		findRejectedTimeSheetForTimeCard,
		fetchUnassignedEntriesFromAccount,
		bulkAssignWorkOrderToEntries,
		deleteTimeSheetEntries,
	} = props;
	const tableRef = React.useRef<TableRef<W_WorkOrderReport_UsersForTimeCardViewTable_VM_Row>>(null);
	const [showUnassignedEntries, setShowUnassignedEntries] = React.useState(false);
	const [entryWorkOrderCombination, setEntryWorkOrderCombination] = React.useState<EntryIdWorkOrderIdCombination>({});
	const [showFooterButton, setShowFooterButton] = React.useState(false);
	const [entryIdListForDeletion, setEntryIdListForDeletion] = React.useState<number[]>([]);
	const [shouldRefresh, setShouldRefresh] = React.useState(false);
	// Account Id is mandatory once the side panel is opened (on table row click) and it is not nullable value. Since it is set once the side panel is
	// opened, I needed to set the default value to be 0 instead of null
	const [selectedAccountId, setSelectedAccountId] = React.useState(0);
	const [currentlyPreviewedSidePanelRow, setCurrentlyPreviewedSidePanelRow] =
		React.useState<Nullable<Row<W_WorkOrderReport_UsersForTimeCardViewTable_VM_Row>>>(null);
	const [entriesFilter, setEntriesFilter] = React.useState<EntriesFilterOptions>(ENTRIES_OPTIONS[0]);

	const saveChanges = React.useCallback(async () => {
		// Remove entries from wo-entry assignment object, whose id is included in array of deleted entries
		const finalWoEntryPairs = entryIdListForDeletion.reduce((newEntryWorkOrderCombination, entryId) => {
			if (entryId in newEntryWorkOrderCombination) {
				delete newEntryWorkOrderCombination[entryId];
			}
			return newEntryWorkOrderCombination;
		}, { ...entryWorkOrderCombination });

		await Promise.all([
			bulkAssignWorkOrderToEntries({ finalWoEntryPairs }, selectedAccountId!),
			deleteTimeSheetEntries({ unassignedEntriesId: entryIdListForDeletion, accountId: selectedAccountId }),
		]);

		setEntryWorkOrderCombination({});
		setEntryIdListForDeletion([]);
		setShouldRefresh(true);
		tableRef.current?.refreshTable();
	}, [bulkAssignWorkOrderToEntries, deleteTimeSheetEntries, entryIdListForDeletion, entryWorkOrderCombination, selectedAccountId]);

	const discardChanges = React.useCallback(() => {
		setEntryIdListForDeletion([]);
		setEntryWorkOrderCombination({});
		setShouldRefresh(true);
	}, []);

	const addNewEntryWoCombination = React.useCallback((entryWoCombination: EntryIdWorkOrderIdCombination) => {
		setEntryWorkOrderCombination((prev) => ({
			...prev,
			...entryWoCombination,
		}));
	}, []);

	const fetchUsersWithEntries = React.useCallback(async (tableQuery: TableQuery):
		Promise<W_WorkOrderReport_UsersForTimeCardViewTable_VM> => {
		const dataForTable = await fetchListOfUsersWithEntries(tableQuery, entriesFilter.option as TimeEntriesFilterType);
		return dataForTable;
	}, [entriesFilter.option, fetchListOfUsersWithEntries]);

	const renderSidePanelContext = React.useCallback((selectedRow: Row<W_WorkOrderReport_UsersForTimeCardViewTable_VM_Row>) => {
		const accountId = selectedRow.original.accountId;

		setSelectedAccountId(accountId);

		if (showUnassignedEntries) {
			return (
				<UnassignedEntriesWrapper
					accountId={accountId}
					entryIdListForDeletion={entryIdListForDeletion}
					fetchUnassignedEntriesFromAccount={fetchUnassignedEntriesFromAccount}
					setEntryIdListForDeletion={setEntryIdListForDeletion}
					setEntryWorkOrderCombination={addNewEntryWoCombination}
					setShouldRefresh={setShouldRefresh}
					shouldRefresh={shouldRefresh}
				/>
			);
		}

		return (
			<TimeCardBodyWrapper
				accountId={accountId}
				fetchAccountTimeCard={fetchAccountTimeCard}
				findRejectedTimeSheetForTimeCard={findRejectedTimeSheetForTimeCard}
			/>
		);

	}, [addNewEntryWoCombination,
		entryIdListForDeletion,
		fetchAccountTimeCard,
		fetchUnassignedEntriesFromAccount,
		findRejectedTimeSheetForTimeCard,
		shouldRefresh,
		setShouldRefresh,
		showUnassignedEntries,
	]);

	const renderFilterMenuItem = React.useCallback((option: EntriesFilterOptions) => {
		return <>{option.label}</>;
	}, []);

	const filterUnassigned = React.useCallback((option: EntriesFilterOptions) => {
		tableRef.current?.resetPagination();
		setEntriesFilter(option);
	}, []);

	const renderFilter = React.useCallback(() => {
		return (
			<div className={bemElement('table-filter', 'parameter', ['no-margin-top', 'margin-right'])}>
				<Dropdown<EntriesFilterOptions>
					className={styles.filterButton}
					defaultValue={ENTRIES_OPTIONS[0]}
					filterable={true}
					id="time-card-unassigned-entries-filter"
					labelKey="option"
					onValueChange={filterUnassigned}
					options={ENTRIES_OPTIONS}
					renderMenuItem={renderFilterMenuItem}
					valueKey="option"
					withBorder={false}
				/>
				<RectangleButton
					action={undefined}
					isLeftOpen={true}
					isSquare={true}
					label={<span className="icon-filter" />}
				/>
			</div>
		);
	}, [filterUnassigned, renderFilterMenuItem]);

	const renderSidePanelHeader = React.useCallback((selectedRow: Row<W_WorkOrderReport_UsersForTimeCardViewTable_VM_Row>) => {
		if (showUnassignedEntries) {
			return <div className={styles.panelHeader}>
				<div>UNASSIGNED TIME ENTRIES | </div>
				<div className={styles.userName}>{`${selectedRow.original.userFullName}`}</div>
			</div>;
		}
		return (
			<div className={styles.panelHeader}>
				<div>TIME CARD VIEW | </div>
				<div className={styles.userName}>{`${selectedRow.original.userFullName}`}</div>
			</div>
		);
	}, [showUnassignedEntries]);

	const closeUnassignedEntriesSidePanel = React.useCallback((row: Row<W_WorkOrderReport_UsersForTimeCardViewTable_VM_Row>) => {
		setCurrentlyPreviewedSidePanelRow(row);
		if (+row.original.numberOfUnassignedEntries) {
			setShowFooterButton(true);
		} else {
			setShowFooterButton(false);
		}
		setShowUnassignedEntries(false);
	}, []);

	const callRenderSidePanelView = React.useCallback((row: Row<W_WorkOrderReport_UsersForTimeCardViewTable_VM_Row>) => (e) => {
		e.stopPropagation();
		setShowUnassignedEntries(true);
		setShowFooterButton(false);
		setCurrentlyPreviewedSidePanelRow(row);
	}, []);

	const openUnassignedEntriesSidePanel = React.useCallback(() => {
		setShowFooterButton(false);
		setShowUnassignedEntries(true);
	}, []);

	const renderSidePanelFooter = React.useCallback(() => {
		return (
			<>
				{showUnassignedEntries &&
					<div className={styles.panelFooter}>
						<Button
							label="Cancel"
							onClick={discardChanges}
							style="secondary"
						/>
						<Button
							label="Save Changes"
							onClick={saveChanges}
							style="primary"
						/>
					</div>
				}
				{showFooterButton &&
					<div className={styles.panelFooter}>
						<Button
							label="Assign Unassigned Entries"
							onClick={openUnassignedEntriesSidePanel}
							style="primary"
						/>
					</div>
				}
			</>
		);

	}, [saveChanges, discardChanges, openUnassignedEntriesSidePanel, showFooterButton, showUnassignedEntries]);

	const renderLinkCell = React.useCallback(() =>
		(_cell: CellContext<W_WorkOrderReport_UsersForTimeCardViewTable_VM_Row, string>) => {
			if (+_cell.cell.row.original.numberOfUnassignedEntries === 0) {
				return (
					<span>
						N/A
					</span>
				);
			}

			return (
				<span className={styles.linkCell} onClick={callRenderSidePanelView(_cell.row)}>
					Unassigned entries ({_cell.cell.row.original.numberOfUnassignedEntries}) {'>'}
				</span>
			);
		}, [callRenderSidePanelView]);

	const renderTextCell = React.useCallback(() => (cell) => {
		return (
			<>
				{cell.row.original.deletedAt && <Tooltip message="User deactivated">
					<span className={`icon-user_deactivated ${styles.deactivatedIcon}`}></span>
				</Tooltip>
				}
				<TextCell className={cell.row.original.deletedAt ? styles.deactivated : styles.fullName} value={cell.getValue()} />
			</>
		);
	}, []);

	const columns: TableProps<W_WorkOrderReport_UsersForTimeCardViewTable_VM_Row>['columns'] = React.useMemo(() => [
		{
			id: 'userFullName',
			header: 'Name',
			accessor: 'userFullName',
			cell: renderTextCell(),
			rightAligned: false,
			enableSorting: true,
		},
		{
			id: 'numberOfUnassignedEntries',
			header: 'Unassigned Time Entries',
			accessor: 'numberOfUnassignedEntries',
			cell: renderLinkCell(),
			enableSorting: true,
		},
	], [renderLinkCell, renderTextCell]);

	return (
		<TableNew
			additionalFilter={renderFilter}
			columns={columns}
			currentlyPreviewedSidePanelRow={currentlyPreviewedSidePanelRow}
			fetch={fetchUsersWithEntries}
			hasSearchInput={true}
			hasSubRows={false}
			onRowClick={closeUnassignedEntriesSidePanel}
			ref={tableRef}
			renderSidePanelContext={renderSidePanelContext}
			renderSidePanelFooter={renderSidePanelFooter}
			renderSidePanelHeader={renderSidePanelHeader}
			selectable={false}
			sidePanelRatio="4fr 2fr"
			sidePanelVisible={true}
			tableName={TableNameEnum.TIME_CARD_VIEW_TABLE}
		/>
	);
};

function mapDispatchToProps() {
	return {
		fetchListOfUsersWithEntries: FieldReportActions.fetchListOfUsersWithEntries,
		fetchAccountTimeCard: TimeCardActions.fetchAccountTimeCard,
		fetchUnassignedEntriesFromAccount: FieldReportActions.fetchUnassignedEntriesFromAccount,
		findRejectedTimeSheetForTimeCard: TimeCardActions.findRejectedTimeSheets,
		bulkAssignWorkOrderToEntries: TimeCardActions.bulkAssignWorkOrderToEntries,
		deleteTimeSheetEntries: TimeCardActions.deleteTimeSheetEntries,
	};
}

const enhance = compose<React.ComponentClass>(
	connector,
	React.memo
);

export default enhance(TimeCardViewTable);
